import { api } from './client';

export type LiveLesson = {
  id: string;
  marathon_id: string;
  curator_id: string;
  title: string;
  topic?: string;
  scheduled_at: string;
  duration_min: number;
  meet_link?: string;
  google_event_id?: string;
  status: 'scheduled' | 'active' | 'finished' | 'cancelled';
};

// One recorded live session (admin recordings view).
export type RecordingRow = {
  id: string;
  lesson_id: string;
  created_by: string;
  created_by_name?: string;
  group?: string;
  title?: string;
  scheduled_start?: string | null;
  recording_started_at?: string;
  ended_at?: string | null;
  duration_sec?: number;
  size_mb?: number;
  participants_count?: number;
  attendees?: { identity: string; name: string }[];
  checked_in?: { identity: string; name: string }[];
  file_name?: string;
  status?: 'recording' | 'completed' | 'failed';
};

// Platform event types (TZ §5.8): webinar / deadline / exam / announcement.
// `personal` is the implicit type of a student's own note.
// `speaking_room` — разговорная комната, которую сервер подмешивает в ленту
// событий из своего расписания комнат. От остальных отличается тем, что
// открывается ВНУТРИ приложения, а не внешней ссылкой (см. `calendarInternalPath`
// в `lib/calendarAccess.ts`).
export type PlatformEventType = 'webinar' | 'deadline' | 'exam' | 'announcement' | 'speaking_room';
export type CalendarEventType = PlatformEventType | 'personal';

export type AudienceType = 'all' | 'marathon' | 'group' | 'tariff';

// CalendarEvent backs both personal student events and platform events.
export type CalendarEvent = {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  start_at: string;
  end_at: string;
  type: CalendarEventType;
  link?: string;
  scope?: 'personal' | 'platform';
  audience_type?: AudienceType;
  marathon_id?: string | null;
  group_id?: string | null;
  tariff?: string;
  created_by?: string;
};

export type PlatformEventInput = {
  title: string;
  description?: string;
  start_at: string; // ISO
  end_at?: string; // ISO
  type: PlatformEventType;
  link?: string;
  audience_type: AudienceType;
  marathon_id?: string;
  group_id?: string;
  tariff?: string;
};

// Color marking by event type (TZ §5.8 colour scheme).
export const EVENT_COLORS: Record<CalendarEventType | 'live_lesson', string> = {
  live_lesson: '#3B82F6', // secondary (blue)
  speaking_room: '#22C55E', // success (green)
  webinar: '#FF941F', // primary (brand orange)
  deadline: '#FF941F',
  exam: '#FF941F',
  announcement: '#FF941F',
  personal: '#5A6172', // on-surface-variant (neutral)
};

// Ключи переводов типов события — сами подписи в locales/eventTypes.
export const EVENT_TYPE_KEYS: Record<CalendarEventType, string> = {
  webinar: 'eventTypes.webinar',
  deadline: 'eventTypes.deadline',
  exam: 'eventTypes.exam',
  announcement: 'eventTypes.announcement',
  personal: 'eventTypes.personal',
  speaking_room: 'schedule.legendSpeakingRoom',
};

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  const items = (data as { items?: T[] } | null)?.items;
  return (items ?? []) as T[];
}

export const calendarApi = {
  // ----- Student -----
  // Backend GET /student/schedule REQUIRES ?marathon_id= and returns a flat
  // array of LiveLesson. marathon_id is not on the auth user, so the caller
  // must pass the active marathon id.
  async liveLessonsForMarathon(marathonId?: string | unknown) {
    if (typeof marathonId !== 'string' || !marathonId) {
      return [] as LiveLesson[];
    }
    const { data } = await api.get('/student/schedule', {
      params: { marathon_id: marathonId },
    });
    return asArray<LiveLesson>(data);
  },
  async getLiveLesson(id: string) {
    const { data } = await api.get(`/student/live-lessons/${id}`);
    return data as LiveLesson;
  },

  // LiveKit join token for a live lesson. `as` picks the role endpoint: a
  // student joins as a participant, a curator/admin joins as host. The video
  // room UI connects to `url` with `token`; media never passes through our API.
  async joinLiveLesson(id: string, as: 'student' | 'curator' = 'student') {
    const { data } = await api.post(`/${as}/live-lessons/${id}/join`);
    return data as { url: string; room: string; token: string; identity: string; host: boolean };
  },
  // ----- Host moderation of a live-lesson room (curator/admin) -----
  async muteRoomParticipant(id: string, identity: string, muted: boolean) {
    await api.post(`/curator/live-lessons/${id}/mute`, { identity, muted });
  },
  async removeRoomParticipant(id: string, identity: string) {
    await api.post(`/curator/live-lessons/${id}/remove`, { identity });
  },

  // ── Recording (LiveKit Egress) ──
  async startRecording(lessonId: string, group: string) {
    const { data } = await api.post(`/curator/live-lessons/${lessonId}/recording/start`, { group });
    return data;
  },
  async stopRecording(lessonId: string) {
    const { data } = await api.post(`/curator/live-lessons/${lessonId}/recording/stop`);
    return data;
  },
  // End the call for everyone (stops recording + closes the room).
  async endLiveLesson(lessonId: string) {
    const { data } = await api.post(`/curator/live-lessons/${lessonId}/end`);
    return data;
  },
  // Admin-only recordings list.
  async listRecordings(limit = 50, offset = 0): Promise<{ items: RecordingRow[]; total: number }> {
    const { data } = await api.get('/admin/recordings', { params: { limit, offset } });
    return {
      items: (data?.items ?? []) as RecordingRow[],
      total: (data?.total ?? 0) as number,
    };
  },

  // Platform events targeted to the student. Scope params (marathon_id, tariff,
  // group_id) are resolved by the page from the student's enrollments; the
  // backend always includes platform-wide ("all") events on top.
  async listPlatformEvents(scope?: { marathonIds?: string[]; tariffs?: string[]; groupIds?: string[] }) {
    const params = new URLSearchParams();
    (scope?.marathonIds ?? []).forEach((m) => params.append('marathon_id', m));
    (scope?.groupIds ?? []).forEach((g) => params.append('group_id', g));
    (scope?.tariffs ?? []).forEach((t) => params.append('tariff', t));
    const { data } = await api.get('/student/calendar/events', { params });
    return asArray<CalendarEvent>(data);
  },

  async listPersonalEvents() {
    const { data } = await api.get('/student/calendar/personal');
    return asArray<CalendarEvent>(data);
  },
  async createPersonal(payload: Partial<CalendarEvent>) {
    const { data } = await api.post('/student/calendar/personal', payload);
    return data as CalendarEvent;
  },
  async updatePersonal(id: string, payload: Partial<CalendarEvent>) {
    const { data } = await api.put(`/student/calendar/personal/${id}`, payload);
    return data as CalendarEvent;
  },
  async deletePersonal(id: string) {
    await api.delete(`/student/calendar/personal/${id}`);
  },

  // ----- Curator (Live lessons) -----
  async listMyLiveLessons() {
    const { data } = await api.get('/curator/live-lessons');
    return asArray<LiveLesson>(data);
  },
  async createLiveLesson(payload: Partial<LiveLesson>) {
    const { data } = await api.post('/curator/live-lessons', payload);
    return data as LiveLesson;
  },
  async bulkCreateLiveLessons(payload: {
    marathon_id: string;
    title: string;
    topic?: string;
    start_date: string; // ISO date
    week_count: number;
    days_of_week: number[]; // 0..6 Sun=0
    duration_min: number;
  }) {
    const { data } = await api.post('/curator/live-lessons/bulk', payload);
    return asArray<LiveLesson>(data);
  },
  async updateLiveLesson(id: string, payload: Partial<LiveLesson>) {
    const { data } = await api.put(`/curator/live-lessons/${id}`, payload);
    return data as LiveLesson;
  },
  async cancelLiveLesson(id: string) {
    await api.delete(`/curator/live-lessons/${id}`);
  },

  // ----- Platform events (Admin / Curator CRUD, TZ §5.8) -----
  // Admin manages all under /admin/calendar-events; curator manages their own
  // under /curator/calendar-events.
  async listAdminPlatformEvents() {
    const { data } = await api.get('/admin/calendar-events');
    return asArray<CalendarEvent>(data);
  },
  async createAdminPlatformEvent(payload: PlatformEventInput) {
    const { data } = await api.post('/admin/calendar-events', payload);
    return data as CalendarEvent;
  },
  async updateAdminPlatformEvent(id: string, payload: Partial<PlatformEventInput>) {
    const { data } = await api.put(`/admin/calendar-events/${id}`, payload);
    return data as CalendarEvent;
  },
  async deleteAdminPlatformEvent(id: string) {
    await api.delete(`/admin/calendar-events/${id}`);
  },

  async listCuratorPlatformEvents() {
    const { data } = await api.get('/curator/calendar-events');
    return asArray<CalendarEvent>(data);
  },
  async createCuratorPlatformEvent(payload: PlatformEventInput) {
    const { data } = await api.post('/curator/calendar-events', payload);
    return data as CalendarEvent;
  },
  async updateCuratorPlatformEvent(id: string, payload: Partial<PlatformEventInput>) {
    const { data } = await api.put(`/curator/calendar-events/${id}`, payload);
    return data as CalendarEvent;
  },
  async deleteCuratorPlatformEvent(id: string) {
    await api.delete(`/curator/calendar-events/${id}`);
  },
};
